import { UnstyledButton } from "@hackthenorth/north";
import { rgba } from "polished";
import React from "react";
import styled, { DefaultTheme } from "styled-components";

type ToggleVariant = "default" | "light";

interface SliderProps {
  isOn: boolean;
  onToggle: () => void;
  /** `light` uses borderPrimary / gray instead of dark brandSecondary. */
  variant?: ToggleVariant;
  disabled?: boolean;
  "aria-label"?: string;
}

const trackColor = (
  theme: DefaultTheme,
  variant: ToggleVariant,
  selected: boolean
) => {
  if (variant === "light") {
    return selected
      ? theme.globalConstants.color.borderPrimary
      : theme.globalConstants.color.backgroundGrayDark;
  }
  return selected
    ? theme.globalConstants.color.brandSecondary
    : rgba(theme.globalConstants.color.brandSecondary, 0.5);
};

const knobColor = (
  theme: DefaultTheme,
  variant: ToggleVariant,
  selected: boolean
) => {
  if (variant === "light") {
    return theme.globalConstants.color.white;
  }
  return selected
    ? theme.globalConstants.color.brandPrimary
    : theme.globalConstants.color.backgroundGray;
};

const Container = styled(UnstyledButton)<{
  selected?: boolean;
  $variant: ToggleVariant;
}>`
  display: flex;
  align-items: center;
  flex: none;
  background: ${({ theme, selected, $variant }) =>
    trackColor(theme, $variant, !!selected)};
  border-radius: 10px;
  height: 14px;
  width: 34px;
  transition: background-color 0.25s ease;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.globalConstants.color.borderFocus};
    outline-offset: 2px;
  }
`;

const Slider = styled.div<{ selected?: boolean; $variant: ToggleVariant }>`
  background: ${({ theme, selected, $variant }) =>
    knobColor(theme, $variant, !!selected)};
  box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.12), 0px 2px 2px rgba(0, 0, 0, 0.24);
  border-radius: 50%;
  height: 20px;
  width: 20px;
  transform: ${({ selected }) =>
    !selected ? "translateX(0)" : "translateX(100%)"};
  transition: transform 0.25s ease, background-color 0.25s ease;
`;

export const ToggleSlider: React.FC<SliderProps> = ({
  isOn,
  onToggle,
  variant = "default",
  disabled = false,
  "aria-label": ariaLabel,
}) => {
  return (
    <Container
      selected={isOn}
      $variant={variant}
      disabled={disabled}
      aria-pressed={isOn}
      aria-label={ariaLabel}
      onClick={() => {
        if (!disabled) onToggle();
      }}
    >
      <Slider selected={isOn} $variant={variant} />
    </Container>
  );
};
